import React from 'react';
import { PageMetaContext } from '../../../context/PageMetaContext';
import { ShareModalFragment } from '../../../../types/__generated__';
import { useTranslation } from 'react-i18next';
import { filterByTypename } from '../../../utils/graphql';
import { generateUrlWithShareParameter } from '../../../utils/generateUrlWithShareParameter';
import { ShareQueryParameter } from '../../../constants/social';
import query from './ShareTimelineEntryModalQuery.gql';
import { ShareModal } from '../../ShareModal/ShareModal';
import {
  useShareUrls,
  usePageOwner,
  useNotNullableContext,
  buildQuery,
  UsePageQueryResult,
  usePageQuery,
} from '../../../hooks';

const FRAGMENT = buildQuery(query);

interface ShareTimelineEntryModalProps {
  onClose: () => void;
  imageUrl?: string;
  description?: string | null;
}

export const ShareTimelineEntryModal: React.FunctionComponent<ShareTimelineEntryModalProps> = ({
  onClose,
  imageUrl,
  description,
}: ShareTimelineEntryModalProps) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const result = usePageQuery<ShareModalFragment>(FRAGMENT);
  const { currentUrl } = useNotNullableContext(PageMetaContext);
  const { isPageOwner } = usePageOwner();

  const metaTagValues: ShareQueryParameter = {
    ogImageUrl: imageUrl?.split('?')[0],
    ogDescription: description || undefined,
  };

  const shareUrl = generateUrlWithShareParameter(currentUrl, metaTagValues);

  const getEmailContent = (shareUrl: string) => {
    let emailMessage: string;
    if (isPageOwner) {
      description
        ? (emailMessage = `${t('fundraising.shareUpdateEmailPageOwner', {
            charityName,
          })}${shareUrl}\n\n"${description}"`)
        : (emailMessage = t('fundraising.shareEmailPageOwner', {
            charityName,
            emailUrl: shareUrl,
          }));
    } else {
      description
        ? (emailMessage = `${t('fundraising.shareUpdateEmailNonPageOwner', {
            name: result.data?.owner?.name,
            charityName,
          })}${shareUrl}\n\n"${description}"`)
        : (emailMessage = t('fundraising.shareEmailNonPageOwner', {
            name: result.data?.owner?.name,
            charityName,
            emailUrl: shareUrl,
          }));
    }
    const body = `${t('common.greeting')},\n\n${emailMessage}\n\n${t(
      'common.thankYou',
    )},\n\n${isPageOwner ? result.data?.owner?.name : ''}`;

    return {
      body,
      subject:
        result.data?.title ?? (t('common.defaultEmailSubject') as string),
    };
  };

  const shareUrls = useShareUrls(shareUrl, {
    email: getEmailContent,
  });

  if (!UsePageQueryResult.hasData(result) || !result.data || !shareUrls) {
    return null;
  }
  const charities = filterByTypename(
    result.data.relationships?.beneficiaries?.nodes ?? [],
    'Charity',
  );

  const charityName = charities[0]?.name
    ? charities[0].name
    : t('charity.defaultName');

  return (
    <ShareModal
      heading={t('timeline.shareModalTitle')}
      description={t('timeline.shareModalMessage')}
      onClose={onClose}
      shareItems={shareUrls}
    />
  );
};
